import type { DocumentLineInput, LineTotals } from './types';

/**
 * Calcule les totaux d'une ligne de document.
 * Logique pure, sans dépendance externe.
 *
 * Deux ancres possibles :
 *
 * — Ancre HT (défaut, `unitPriceTtc` absent) :
 *   totalHtBeforeDiscount = qty * unitPriceHt
 *   discountAmount = mode 'amount' ? discountAmount : totalHtBeforeDiscount * discountPct / 100
 *                    (borné à totalHtBeforeDiscount ; une remise NÉGATIVE —
 *                    majoration, vente au-dessus du tarif — est conservée)
 *   totalHt = totalHtBeforeDiscount - discountAmount
 *   vatAmount = totalHt * vatRate / 100
 *   totalTtc = totalHt + vatAmount
 *
 * — Ancre TTC (`unitPriceTtc` > 0, prix catalogue TTC en millimes) :
 *   totalTtc = qty * round3(unitPriceTtc) - remise (remise 'amount' côté TTC)
 *   totalHt = totalTtc / (1 + vatRate/100)   (dérivé)
 *   vatAmount = totalTtc - totalHt
 *   → 100 pièces à 1,000 DT TTC = 100,000 DT exactement, quel que soit
 *     l'arrondi millime du PU HT stocké. `discountAmount` est rapporté en
 *     équivalent HT pour que les statistiques document restent homogènes.
 *
 * La remise ligne s'exprime soit en pourcentage (`discountMode` 'pct', défaut),
 * soit en montant fixe DT (`discountMode` 'amount', via `discountAmount`).
 * Une remise négative (majoration) est honorée : des documents réels en
 * portent (ex. −10 % = +10 % sur le prix) et l'écraser à zéro ferait dériver
 * le recalcul des totaux par rapport aux totaux enregistrés.
 *
 * Arrondi : 3 décimales (millimes tunisiens)
 */
export function calculateLineTotals(line: DocumentLineInput): LineTotals {
  const anchorTtc = Number(line.unitPriceTtc);
  if (Number.isFinite(anchorTtc) && anchorTtc > 0) {
    return calculateTtcAnchoredTotals(line, anchorTtc);
  }

  const totalHtBeforeDiscount = round3(line.quantity * line.unitPriceHt);
  const rawDiscount = line.discountMode === 'amount'
    ? (line.discountAmount ?? 0)
    : totalHtBeforeDiscount * (line.discountPct ?? 0) / 100;
  // Borne haute : jamais supérieure au sous-total. Pas de borne basse — une
  // remise négative est une majoration voulue.
  const discountAmount = round3(Math.min(rawDiscount, totalHtBeforeDiscount));
  const totalHt = round3(totalHtBeforeDiscount - discountAmount);
  const vatAmount = round3(totalHt * line.vatRate / 100);
  const totalTtc = round3(totalHt + vatAmount);

  return {
    totalHtBeforeDiscount,
    discountAmount,
    totalHt,
    totalTtc,
    vatAmount,
  };
}

function calculateTtcAnchoredTotals(line: DocumentLineInput, anchorTtc: number): LineTotals {
  const vatFactor = 1 + line.vatRate / 100;
  const unitTtc = round3(anchorTtc);
  const totalTtcBeforeDiscount = round3(line.quantity * unitTtc);
  const rawDiscountTtc = line.discountMode === 'amount'
    ? (line.discountAmount ?? 0)
    : totalTtcBeforeDiscount * (line.discountPct ?? 0) / 100;
  // Même règle que côté HT : borne haute seulement, majoration conservée.
  const discountTtc = round3(Math.min(rawDiscountTtc, totalTtcBeforeDiscount));
  const totalTtc = round3(totalTtcBeforeDiscount - discountTtc);
  const totalHt = round3(totalTtc / vatFactor);
  const vatAmount = round3(totalTtc - totalHt);

  return {
    totalHtBeforeDiscount: round3(totalTtcBeforeDiscount / vatFactor),
    discountAmount: round3(discountTtc / vatFactor),
    totalHt,
    totalTtc,
    vatAmount,
  };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
